import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { ShieldIcon, TruckIcon, ZapIcon, MapPinIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
interface AboutPageProps {
  setPage: (page: string) => void;
}
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function AboutPage({
  setPage
}: AboutPageProps) {
  return <div className="w-full bg-white" data-id="element-110">
      {/* Hero Section */}
      <section className="bg-charcoal pt-20 pb-16 px-4" data-id="element-111">
        <div className="max-w-7xl mx-auto text-center" data-id="element-112">
          <motion.h1 initial="hidden" animate="visible" variants={fadeInUp} className="text-4xl md:text-6xl font-heading font-extrabold text-white uppercase tracking-tight mb-4" data-id="element-113">
            About <span className="text-brand-red" data-id="element-114">Merem Blasting</span>
          </motion.h1>
          <motion.p initial="hidden" animate="visible" variants={fadeInUp} className="text-lg text-steel-300 font-body max-w-2xl mx-auto" data-id="element-115">
            Michigan's premier mobile surface preparation company. We bring
            industrial-grade blasting directly to your site.
          </motion.p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 px-4" data-id="element-116">
        <div className="max-w-7xl mx-auto" data-id="element-117">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-118">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="relative h-[500px] rounded-sm overflow-hidden shadow-2xl" data-id="element-119">
              <img src="https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?q=80&w=1000&auto=format&fit=crop" alt="Merem Blasting mobile unit" className="w-full h-full object-cover" data-id="element-120" />
              <div className="absolute inset-0 bg-brand-red/10 mix-blend-multiply" data-id="element-121"></div>
              <div className="absolute bottom-6 left-6 bg-white p-4 shadow-lg border-l-4 border-brand-red" data-id="element-122">
                <p className="font-heading font-bold uppercase text-charcoal text-xl" data-id="element-123">
                  Peter Merem
                </p>
                <p className="text-steel-500 text-sm font-medium" data-id="element-124">
                  Owner & Operator
                </p>
              </div>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={staggerContainer} data-id="element-125">
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-heading font-extrabold uppercase text-charcoal mb-6" data-id="element-126">
                Real Results. No Corporate Fluff.
              </motion.h2>
              <motion.div variants={fadeInUp} className="space-y-6 text-steel-600 leading-relaxed text-lg" data-id="element-127">
                <p data-id="element-128">
                  Merem Mobile Dustless Blasting Michigan, LLC was built on a
                  simple premise: provide fast, reliable, and highly effective
                  surface preparation services directly to the customer's
                  location.
                </p>
                <p data-id="element-129">
                  Based in Fowlerville, Michigan, owner Peter Merem and his team
                  travel across the entire state, bringing state-of-the-art
                  dustless and dry blasting equipment to industrial sites,
                  commercial properties, farms, and residential locations.
                </p>
                <p data-id="element-130">
                  We understand that downtime costs money. Whether you're
                  restoring a classic car, prepping a fleet of heavy machinery,
                  or removing graffiti from a historic building, you need a team
                  that shows up on time, works safely, and leaves a clean,
                  paint-ready surface.
                </p>
              </motion.div>

              <motion.div variants={fadeInUp} className="mt-10" data-id="element-131">
                <Button variant="primary" onClick={() => setPage('contact')} data-id="element-132">
                  Work With Us
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-steel-50 px-4 border-y border-steel-200" data-id="element-133">
        <div className="max-w-7xl mx-auto" data-id="element-134">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-135">
            <h2 className="text-3xl md:text-4xl font-heading font-extrabold uppercase text-charcoal mb-4" data-id="element-136">
              Our Commitment to the Job
            </h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-137">
            <motion.div variants={fadeInUp} className="bg-white p-8 border border-steel-100 text-center" data-id="element-138">
              <div className="w-16 h-16 bg-steel-50 rounded-full flex items-center justify-center mx-auto mb-6 border border-steel-200" data-id="element-139">
                <ZapIcon className="w-8 h-8 text-brand-red" data-id="element-140" />
              </div>
              <h3 className="text-xl font-heading font-bold uppercase text-charcoal mb-4" data-id="element-141">
                Fast Turnaround
              </h3>
              <p className="text-steel-600" data-id="element-142">
                We pride ourselves on quick estimates and efficient on-site
                work. We respect your timeline and work diligently to keep your
                project moving forward.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-white p-8 border border-steel-100 text-center" data-id="element-143">
              <div className="w-16 h-16 bg-steel-50 rounded-full flex items-center justify-center mx-auto mb-6 border border-steel-200" data-id="element-144">
                <ShieldIcon className="w-8 h-8 text-brand-red" data-id="element-145" />
              </div>
              <h3 className="text-xl font-heading font-bold uppercase text-charcoal mb-4" data-id="element-146">
                Safety & Compliance
              </h3>
              <p className="text-steel-600" data-id="element-147">
                OSHA-conscious and safety-forward. Our dustless process requires
                less containment, reduces airborne hazards, and is EPA/DEQ
                conscious.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-white p-8 border border-steel-100 text-center" data-id="element-148">
              <div className="w-16 h-16 bg-steel-50 rounded-full flex items-center justify-center mx-auto mb-6 border border-steel-200" data-id="element-149">
                <TruckIcon className="w-8 h-8 text-brand-red" data-id="element-150" />
              </div>
              <h3 className="text-xl font-heading font-bold uppercase text-charcoal mb-4" data-id="element-151">
                Fully Mobile
              </h3>
              <p className="text-steel-600" data-id="element-152">
                We bring the blast yard to you. Our self-contained mobile units
                have everything needed to complete the job on your site,
                anywhere in Michigan.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Service Area Banner */}
      <section className="py-16 bg-charcoal text-white text-center px-4" data-id="element-153">
        <div className="max-w-4xl mx-auto" data-id="element-154">
          <MapPinIcon className="w-12 h-12 text-brand-red mx-auto mb-6" data-id="element-155" />
          <h2 className="text-3xl font-heading font-extrabold uppercase mb-4" data-id="element-156">
            Based in Fowlerville. Serving All of Michigan.
          </h2>
          <p className="text-steel-300 text-lg mb-8" data-id="element-157">
            From Detroit to Grand Rapids, Lansing to Traverse City, we travel
            state-wide to handle your surface preparation needs.
          </p>
          <Button variant="primary" onClick={() => setPage('contact')} data-id="element-158">
            Request an On-Site Estimate
          </Button>
        </div>
      </section>
    </div>;
}